import { Injectable } from '@angular/core';
import { ApiService } from 'src/app/core/api/api.service';
import { endpoints } from 'src/app/core/config/endpoints';
import { DateHelperService } from 'src/app/core/helpers/date-helper/date-helper.service';
import { LoggerService } from 'src/app/core/logger/logger.service';
import { AppointmentTimeEntry } from 'src/app/core/models/appointment-time-entry.model';
import { TimeEntry, TimeEntryAddHttp, TimeEntryEditByIdHttp, TimeEntryEditByInfoHttp } from 'src/app/core/models/time-entry.model';
import { HoursMinutes } from 'src/app/core/models/time.model';
import { AppointmentService } from 'src/app/core/services/appointment/appointment.service';
import { LocalStorage } from 'src/app/core/storage/local-storage/local-storage';
import { LocalStorageService } from 'src/app/core/storage/local-storage/local-storage.service';
import { AuthService } from '../authentication/services/auth.service';
import { Appointment } from './../models/appointment.model';

@Injectable({
	providedIn: 'root'
})
export class TimeEntryService {

	table = 'timeEntries';
	private store: LocalStorage;

	constructor(
		private apiService: ApiService,
		private storage: LocalStorageService,
		private logger: LoggerService,
		private dateHelper: DateHelperService,
		private appointmentService: AppointmentService,
		private authService: AuthService,
	) {
		this.store = this.storage[this.table];
	}

	async getAll(): Promise<AppointmentTimeEntry[]> {
		return await this.store.getAll();
	}

	async getAppointmentTimeEntry(apptId: number): Promise<AppointmentTimeEntry> {
		return await this.store.getItem(apptId);
	}

	async newAppointmentTimeEntry(appointment: Appointment): Promise<AppointmentTimeEntry> {
		return new AppointmentTimeEntry({
			apptId: appointment.id,
			apptStart: appointment.apptStart,
			displayValue: appointment.displayValue,
			techId: await this.authService.getCurrentUserId(),
		});
	}

	getAppointmentTimeEntryTime(apptTimeEntry: AppointmentTimeEntry): HoursMinutes {
		let totalMinutes = 0;
		for (const timeEntry of apptTimeEntry.entries) {
			totalMinutes = totalMinutes + timeEntry.minutes;
		}

		return this.dateHelper.getHoursMinutesFromMinutes(totalMinutes);
	}

	getTotalTimeToday(apptTimeEntries: AppointmentTimeEntry[]): HoursMinutes {
		let totalMinutes = 0;
		for (const apptTimeEntry of apptTimeEntries) {
			for (const timeEntry of apptTimeEntry.entries) {
				totalMinutes = totalMinutes + timeEntry.minutes;
			}
		}

		return this.dateHelper.getHoursMinutesFromMinutes(totalMinutes);
	}

	async saveTimeEntry(techId: number, apptId: number, entry: TimeEntry, mode: string, callback: () => void): Promise<void> {
		await this.saveTimeEntryLocal(techId, apptId, entry, mode);

		this.saveTimeEntryHTTP(techId, apptId, entry, mode, callback);
	}

	async saveTimeEntryLocal(techId: number, apptId: number, entry: TimeEntry, mode: string): Promise<void> {
		let apptTimeEntry = await this.getAppointmentTimeEntry(apptId);
		if (!apptTimeEntry) {
			apptTimeEntry = new AppointmentTimeEntry();

			if (apptId !== -2000) { // 'Other' appointment
				const appt = await this.appointmentService.getLocalAppointment(apptId);
				apptTimeEntry.apptId = apptId;
				apptTimeEntry.displayValue = appt.displayValue;
				apptTimeEntry.apptStart = appt.apptStart;
				apptTimeEntry.techId = techId;
			}
		}

		if (mode === 'Edit') {
			const index = (entry.id !== 0)
				? apptTimeEntry.entries.findIndex(te => te.id === entry.id)
				: apptTimeEntry.entries.findIndex(te => te.time === entry.time);

			apptTimeEntry.entries[index] = entry;
		} else {
			apptTimeEntry.entries.push(entry);
		}

		this.store.setItem(apptId, apptTimeEntry);
	}

	saveTimeEntryHTTP(techId: number, apptId: number, entry: TimeEntry, mode: string, callback: () => void): void {
		if (mode === 'Add') {
			const url = endpoints.appointment.timeEntry.add;

			this.apiService.post<number>(url, new TimeEntryAddHttp(techId, apptId, entry))
				.subscribe(
					async id => {
						await this.updateTimeEntry(apptId, { ...entry, id });
						callback();
					},
					err => this.logger.logException(err),
				);
		}

		if (mode === 'Edit') {
			if (entry.id === 0) { // Edit by entry info
				const url = endpoints.appointment.timeEntry.editByInfo;
				const body = new TimeEntryEditByInfoHttp(techId, apptId, entry);

				this.apiService.put(url, body)
					.subscribe(
						_ => callback(),
						err => this.logger.logException(err),
					);

			} else { // Edit by ID
				const url = endpoints.appointment.timeEntry.editById.replace(':entryId', `${entry.id}`);
				const body = new TimeEntryEditByIdHttp(entry);

				this.apiService.put(url, body)
					.subscribe(
						_ => callback(),
						err => this.logger.logException(err),
					);

			}
		}
	}

	async updateTimeEntry(apptId: number, entry: TimeEntry): Promise<void> {
		const apptTimeEntry = await this.getAppointmentTimeEntry(apptId);
		const entryIndex = apptTimeEntry.entries.findIndex(e => e.time === entry.time);
		if (entryIndex === -1) {
			return;
		}
		apptTimeEntry.entries[entryIndex] = entry;

		this.store.setItem(apptId, apptTimeEntry);
	}

	async deleteTimeEntry(timeEntry: TimeEntry, apptId: number, techId: number) {
		if (timeEntry.id === 0) {
			const body = {
				appointmentId: apptId,
				timeCodeId: timeEntry.timeCodeId,
				techId,
				time: timeEntry.time,
			};
			await this.deleteTimeEntryByInfo(body);
		} else {
			await this.deleteTimeEntryById(timeEntry.id);
		}
		await this.removeTimeEntryFromLocal(apptId, timeEntry);
	}

	private async deleteTimeEntryById(entryId: number) {
		const url = endpoints.appointment.timeEntry.deleteById.replace(':entryId', `${entryId}`);
		return this.apiService.delete(url, {});
	}

	private async deleteTimeEntryByInfo(body: any) {
		const url = endpoints.appointment.timeEntry.deleteByInfo;
		return this.apiService.delete(url, body);
	}

	private async removeTimeEntryFromLocal(apptId: number, entry: TimeEntry) {
		const apptTimeEntry = await this.getAppointmentTimeEntry(apptId);
		apptTimeEntry.entries = apptTimeEntry.entries.filter(x => x.time !== entry.time);

		await this.store.setItem(apptId, apptTimeEntry);
	}

}
